// Cross-tab intra-SPA navigation helper.
//
// Used when a UI element on one tab needs to deep-link into another tab and
// scroll to / highlight a specific row. Example: a Spec Compliance flag on
// the Scope tab linking to its source subsection in the Spec Book tab, or
// to its plan sheet in the Plans tab.
//
// Mechanism:
//   1. Caller sets the target via `navigateTo({ tab, target })`. We persist
//      to sessionStorage AND fire a `heritage-tab-change` window event with
//      the target tab. App.tsx listens for the event and calls setTab.
//   2. The receiving tab calls `consumeNavTarget(thisTab)` once on mount.
//      Returns the target object if one is queued for it, or null. Clears
//      sessionStorage on consume so a refresh doesn't re-trigger the scroll.

const KEY = 'heritage_nav_target'
const EVENT = 'heritage-tab-change'

export type NavTarget = {
  tab: string
  // Spec Book navigation
  specSubsectionId?: string
  // Plans navigation
  planVersionId?: string
  planSheetId?: string
  planSheetPage?: number
}

export function navigateTo(target: NavTarget): void {
  sessionStorage.setItem(KEY, JSON.stringify(target))
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { tab: target.tab } }))
}

export function consumeNavTarget(forTab: string): NavTarget | null {
  const raw = sessionStorage.getItem(KEY)
  if (!raw) return null
  try {
    const t = JSON.parse(raw) as NavTarget
    if (t.tab !== forTab) return null
    sessionStorage.removeItem(KEY)
    return t
  } catch {
    sessionStorage.removeItem(KEY)
    return null
  }
}

export const NAV_TAB_CHANGE_EVENT = EVENT

// Brief yellow highlight on a target element. 2.5s fade — long enough for
// the eye to land on it, short enough not to be annoying.
export function flashHighlight(el: HTMLElement | null): void {
  if (!el) return
  const prev = el.style.transition
  const prevBg = el.style.backgroundColor
  el.style.transition = 'background-color 0.2s ease-in'
  el.style.backgroundColor = 'rgba(255, 230, 100, 0.55)'
  setTimeout(() => {
    el.style.transition = 'background-color 2.2s ease-out'
    el.style.backgroundColor = prevBg
  }, 250)
  setTimeout(() => {
    el.style.transition = prev
  }, 2500)
}
